
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import Toast from '@/utils/toast';
import CreateAnnouncement from './CreateAnnouncement.vue';
import ManageAnnouncements from './ManageAnnouncements.vue';
import StandardModal from '../shared/suites/StandardModal.vue';

const namespace: string = 'announcements';

@Component<AnnouncementModal>({
	components: {
		CreateAnnouncement,
		ManageAnnouncements,
		StandardModal
	}
})
export default class AnnouncementModal extends Vue {
	@Action('fetchAnnouncements', { namespace }) private fetchAnnouncements!: () => Promise<void>;
	@Action('createAnnouncement', { namespace }) private createAnnouncement!: (announcement: AnnouncementPayload) => Promise<void>;
	@Getter('getAnnouncements', { namespace }) private announcements!: Announcement[];
	@Prop({ type: Boolean, required: true, default: false }) private showModal!: boolean;

	$refs!: { announcementForm: CreateAnnouncement; };

	private loading: boolean = false;
	private creatingAnnouncement: boolean = false;
	private announcement: AnnouncementPayload = {
		message: {},
		restaurantIds: []
	};
	private modalProps: { [key: string]: any } = {
		loading: false,
		title: this.$t('settings.locations.announcements.manage.title'),
		cancelButtonText: '',
		confirmButtonText: '',
		disableConfirm: true,
		size: 'medium'
	};
	private modalEvents: { [key: string]: Function } = {
		cancel: () => this.closeModal(),
		close: () => this.closeModal(),
		confirm: () => this.closeModal()
	};

	private async created(): Promise<void> {
		try {
			this.loading = true;
			await this.fetchAnnouncements();
		}
		catch (error) {
			Toast.error(this.$t('settings.locations.announcements.manage.error_fetching_announcements'));
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Update announcement payload and determine if confirm button should be disabled
	 *
	 * @param {AnnouncementPayload} announcement
	 * @return {void}
	 */
	private handleAnnouncementChange(announcement: AnnouncementPayload): void {
		this.announcement = announcement;
		this.modalProps.disableConfirm = !this.announcement.restaurantIds.length;
	}

	/**
	 * Set modal props and events to display create announcement form
	 *
	 * @return {void}
	 */
	private setCreateAnnouncement(): void {
		this.creatingAnnouncement = true;
		this.modalProps = {
			...this.modalProps,
			title: this.$t('settings.locations.announcements.create.title'),
			cancelButtonText: this.$t('shared.actions_array.btn_discard'),
			confirmButtonText: this.$t('shared.actions_array.btn_submit')
		};
		this.modalEvents = {
			...this.modalEvents,
			confirm: () => this.submitAnnouncement(),
			cancel: () => this.resetModal()
		};
	}

	/**
	 * Reset modal props and events to default state (manage announcements)
	 *
	 * @return {void}
	 */
	private resetModal(): void {
		this.creatingAnnouncement = false;
		this.announcement = {
			message: {},
			restaurantIds: []
		};
		this.modalProps = {
			...this.modalProps,
			title: this.$t('settings.locations.announcements.manage.title'),
			cancelButtonText: '',
			confirmButtonText: '',
			disableConfirm: true
		};
		this.modalEvents = {
			...this.modalEvents,
			confirm: () => this.closeModal(),
			cancel: () => this.closeModal()
		};
	}

	/**
	 * Reset modal to default state and close it
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.resetModal();
		this.$emit('close');
	}

	/**
	 * Validate announcement form and submit announcement if valid
	 *
	 * @return {Promise<void>}
	 */
	private async submitAnnouncement(): Promise<void> {
		try {
			this.modalProps.loading = true;
			// Validate announcement form (validateAnnouncement is a method in CreateAnnouncement component)
			const valid = this.$refs.announcementForm.validateAnnouncement();
			if (valid) {
				await this.createAnnouncement(this.announcement);
				this.creatingAnnouncement = false;
				this.resetModal();
			}
		}
		catch (error) {
			Toast.error(this.$t('settings.locations.announcements.create.error_creating_announcement'));
		}
		finally {
			this.modalProps.loading = false;
		}
	}
}
